/**
 * Solanaland
 *
 * Author: Phia Nasol
 * Company: Digiteer Inc.
 *
 * https://www.digiteer.digital/
 *
 */

import React from "react";
import { Col, Container, Row } from "react-bootstrap";
import { api } from "core/api";
import { ControlledTable, Select, SvgIcon } from "core/components";
import {
  admin_warehouse_inventory_index_path,
  edit_admin_inventory_item_path,
  edit_admin_project_path,
  edit_admin_purchase_order_path,
  history_admin_item_inventory_path,
  print_admin_material_return_path,
  print_issuance_admin_material_request_path,
  print_material_direct_site_delivery_path,
  print_receiving_slip_site_delivery_path
} from "routes";
import moment from "moment";
import { Action } from "core/schema";
import formatter from "core/formatter";
import ControlledTableContext from "core/components/ControlledTableContext";

type ItemInventoryHistoryProps = {
  inventory_item: Record<string, unknown>;
  has_item_inventory_histories: boolean;
};

const ItemInventoryHistory = ({ inventory_item, warehouse_id, warehouses, has_item_inventory_histories }: ItemInventoryHistoryProps): JSX.Element => {
  const [isLoading, setIsLoading] = React.useState(false);
  const [itemInventoryHistory, setItemInventoryHistory] = React.useState([]);
  const [warehouseFilter, setWarehouseFilter] = React.useState("");
  const [pagination, setPagination] = React.useState(null);
  const [currentPage, setCurrentPage] = React.useState(1);
  const table = React.useRef(null);
  const columns = React.useMemo(
    () => [
      {
        id: "id",
        Header: "Id",
        accessor: "id"
      },
      {
        id: "warehouse",
        Header: "Warehouse",
        accessor: "warehouse.id",
        Cell: () => {
          return <></>;
        }
      },
      {
        id: 1,
        Header: "Warehouse",
        accessor: "warehouse.name",
        Cell: (props) => {
          return (
            <div title={props.value} data-bs-toggle="tooltip">
              <p className="text-truncate text-center">{props.value}</p>
            </div>
          );
        }
      },
      {
        id: 3,
        Header: "Adjustment Reason",
        accessor: "adjustment_reason_name",
        Cell: (props) => {
          return (
            <div title={props.value} data-bs-toggle="tooltip">
              <p className="text-truncate text-center">{props.value}</p>
            </div>
          );
        }
      },
      {
        Header: "Adjustment",
        accessor: "adjustment",
        Cell: (props) => {
          const adjustment = props.row.original.action == Action.Add ? props.value : `-${props.value}`;

          return (
            <div title={adjustment} data-bs-toggle="tooltip">
              <p className="text-truncate text-center">{adjustment}</p>
            </div>
          );
        }
      },
      {
        Header: "New Qty",
        accessor: "quantity",
        Cell: (props) => {
          return (
            <div title={props.value} data-bs-toggle="tooltip">
              <p className="text-truncate text-center">{props.value}</p>
            </div>
          );
        }
      },
      {
        Header: "Total Qty",
        accessor: "total_quantity",
        Cell: (props) => {
          const totalQuantity = (+props.value).toLocaleString("en-US", { minimumFractionDigits: 1 });
          return (
            <div title={totalQuantity} data-bs-toggle="tooltip">
              <p className="text-truncate text-center">{totalQuantity}</p>
            </div>
          );
        }
      },
      {
        Header: "Transaction Unit Cost",
        accessor: "transaction_unit_cost",
        Cell: (props) => {
          const transactionUnitCost = props.value ? formatter.format(parseFloat(props.value)) : "N/A";

          return (
            <div title={transactionUnitCost} data-bs-toggle="tooltip">
              <p className="text-truncate text-center">{transactionUnitCost}</p>
            </div>
          );
        }
      },
      {
        Header: "Transaction Cost",
        accessor: "transaction_cost",
        Cell: (props) => {
          const transactionCost = formatter.format(parseFloat(props.value));

          return (
            <div title={transactionCost} data-bs-toggle="tooltip">
              <p className="text-truncate text-center">{transactionCost}</p>
            </div>
          );
        }
      },
      {
        Header: "Carrying Cost",
        accessor: "carrying_cost",
        Cell: (props) => {
          const carryingCost = props.value ? formatter.format(parseFloat(props.value)) : "N/A";

          return (
            <div title={carryingCost} data-bs-toggle="tooltip">
              <p className="text-truncate text-center">{carryingCost}</p>
            </div>
          );
        }
      },
      {
        Header: "Carrying Unit Price",
        accessor: "moving_average_price",
        Cell: (props) => {
          const movingAveragePrice = props.value ? formatter.format(parseFloat(props.value)) : "N/A";

          return (
            <div title={movingAveragePrice} data-bs-toggle="tooltip">
              <p className="text-truncate text-center">{movingAveragePrice}</p>
            </div>
          );
        }
      },
      {
        Header: "Adjusted By",
        accessor: "created_by",
        Cell: (props) => {
          return (
            <div title={props.value} data-bs-toggle="tooltip">
              <p className="text-truncate text-center">{props.value}</p>
            </div>
          );
        }
      },
      {
        Header: "Adjustment Date",
        accessor: "created_at",
        Cell: (props) => {
          const createdAt = moment(props.value).format("MM/DD/YYYY hh:mmA");

          return (
            <div title={createdAt} data-bs-toggle="tooltip">
              <p className="text-center text-truncate">{createdAt}</p>
            </div>
          );
        }
      },
      {
        id: 2,
        Header: "Reference",
        accessor: "metadata",
        Cell: (props) => {
          if (props.value) {
            const metadata = JSON.parse(props.value);

            if (metadata) {
              if (metadata?.project_id) {
                return (
                  <div title={`Project ${metadata.project_code || ""}`} data-bs-toggle="tooltip">
                    <p className="text-truncate text-center">
                      <a href={edit_admin_project_path(metadata.project_id)} target="_blank" rel="noopener noreferrer">{`PROJECT#${
                        metadata.project_code || ""
                      }`}</a>
                    </p>
                  </div>
                );
              } else if (metadata.delivery_id) {
                if (metadata.receiving_slip_code) {
                  return (
                    <div title={metadata.receiving_slip_code} data-bs-toggle="tooltip">
                      <p className="text-truncate text-center">
                        <a href={print_receiving_slip_site_delivery_path(metadata.delivery_id)} target="_blank" rel="noopener noreferrer">
                          {metadata.receiving_slip_code}
                        </a>
                      </p>
                    </div>
                  );
                } else if (metadata.material_direct_code) {
                  const project_code = metadata.project_code;
                  const project_name = metadata.project_name;

                  return (
                    <div title={metadata.material_direct_code} data-bs-toggle="tooltip">
                      <p className="text-truncate text-center">
                        <a
                          href={print_material_direct_site_delivery_path(metadata.delivery_id, { project_code, project_name })}
                          target="_blank"
                          rel="noopener noreferrer"
                        >
                          {metadata.material_direct_code}
                        </a>
                      </p>
                    </div>
                  );
                }
                return "";
              } else if (metadata.purchase_order_id) {
                return (
                  <div title={metadata.purchase_order_code} data-bs-toggle="tooltip">
                    <p className="text-truncate text-center">
                      <a href={edit_admin_purchase_order_path(metadata.purchase_order_id)} target="_blank" rel="noopener noreferrer">
                        {metadata.purchase_order_code}
                      </a>
                    </p>
                  </div>
                );
              } else if (metadata.material_request_id) {
                return (
                  <div title={metadata.issuance_code} data-bs-toggle="tooltip">
                    <p className="text-truncate text-center">
                      <a href={print_issuance_admin_material_request_path(metadata.material_request_id)} target="_blank" rel="noopener noreferrer">
                        {metadata.issuance_code}
                      </a>
                    </p>
                  </div>
                );
              } else if (metadata.material_return_id) {
                return (
                  <div title={metadata.return_slip_code} data-bs-toggle="tooltip">
                    <p className="text-truncate text-center">
                      <a href={print_admin_material_return_path(metadata.material_return_id)} target="_blank" rel="noopener noreferrer">
                        {metadata.return_slip_code}
                      </a>
                    </p>
                  </div>
                );
              } else if (metadata.inventory_item_id) {
                const inventoryItem = `${metadata.inventory_item_code} - ${metadata.inventory_item_name}`;

                return (
                  <div title={inventoryItem} data-bs-toggle="tooltip">
                    <p className="text-truncate text-center">
                      <a href={edit_admin_inventory_item_path(metadata.inventory_item_id)} target="_blank" rel="noopener noreferrer">
                        {inventoryItem}
                      </a>
                    </p>
                  </div>
                );
              }
              return (
                <div title={`Warehouse ${metadata.warehouse_code || ""}`} data-bs-toggle="tooltip">
                  <p className="text-truncate text-center">
                    <a href={admin_warehouse_inventory_index_path()} target="_blank" rel="noopener noreferrer">{`WAREHOUSE#${
                      metadata.warehouse_code || ""
                    }`}</a>
                  </p>
                </div>
              );
            }

            return (
              <div title="Manual Adjustment" data-bs-toggle="tooltip">
                <p className="text-truncate text-center">Manual Adjustment</p>
              </div>
            );
          }

          return (
            <div title="-" data-bs-toggle="tooltip">
              <p className="text-truncate text-center">-</p>
            </div>
          );
        }
      }
    ],
    []
  );

  const getItemInventoryHistory = React.useCallback(async ({ page, per, filters }) => {
    try {
      setIsLoading(true);
      const response = await api.get(
        history_admin_item_inventory_path(inventory_item.id, { warehouse_id, page, per, filters: JSON.stringify(filters) })
      );
      setItemInventoryHistory(response.data.data);
      setPagination(response.data.pagination);
      setCurrentPage(response.data.pagination?.current || currentPage);
      return { pagination: response.data.pagination, data: response.data.data };
    } catch (error) {
      console.error(error);
    } finally {
      setIsLoading(false);
    }
  }, []);

  const noContent = () => {
    return (
      <Container fluid={true} className="text-center pt-lg-8 pb-lg-8">
        <Row>
          <Col>
            <SvgIcon name="inventory" className="display-icon text-gray" />
          </Col>
        </Row>
        <Row>
          <Col>
            <h5>No inventory movement.</h5>
          </Col>
        </Row>
      </Container>
    );
  };

  const dataFilter = () => {
    return (
      <Row className="mb-3">
        <Col sm={3}>
          <Select
            placeholder="Filter by Warehouse"
            options={warehouses}
            isSearchable={false}
            getOptionLabel={(option) => option.name}
            getOptionValue={(option) => option.id}
            isOptionDisabled={(option) => !option.active}
            onChange={(newOption) => {
              if (newOption) {
                setWarehouseFilter(newOption.id);
                table.current.setFilter("warehouse", newOption.id);
              } else {
                setWarehouseFilter("");
                table.current.setFilter("warehouse", "");
              }
            }}
            value={warehouses.find((warehouse) => warehouse.id == warehouseFilter) || ""}
          />
        </Col>
      </Row>
    );
  };

  const dataTable = () => {
    return (
      <Row>
        <Col>
          <ControlledTableContext.Provider value={{ currentPage, setCurrentPage }}>
            <ControlledTable
              columns={columns}
              data={itemInventoryHistory}
              ref={table}
              hiddenColumns={["warehouse"]}
              isLoading={isLoading}
              fetchData={getItemInventoryHistory}
              totalCount={pagination?.total_count}
              totalPageCount={pagination?.total_pages || 0}
              previous={pagination?.previous}
              next={pagination?.next}
            />
          </ControlledTableContext.Provider>
        </Col>
      </Row>
    );
  };

  const content = () => {
    return has_item_inventory_histories ? (
      <>
        {!warehouse_id && dataFilter()}
        {dataTable()}
      </>
    ) : (
      noContent()
    );
  };

  return <React.StrictMode>{content()}</React.StrictMode>;
};

export default ItemInventoryHistory;
